import { X } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import ConfirmationModal from "../../../../components/ConfirmationModal";
import { useTheme } from "../../../../contexts/ThemeContext";
import { useToast } from "../../../../contexts/ToastContext";
import { formatNumberFixed } from "../../../../utils/numberUtils";
import {
  StockOverrideCreateInput,
  StockOverrideUpdateInput,
} from "../../../../types/historicalOverrides";
import ActionBar from "./ActionBar";
import ExistingOverrides from "./ExistingOverrides";
import OverrideForm from "./OverrideForm";
import PriceChart from "./PriceChart";
import PriceTable from "./PriceTable";
import TimeRangeBar from "./TimeRangeBar";
import {
  EnrichedPriceRow,
  computeInitialRange,
  findCarryForwardPrices,
  resolveOverrideForSlot,
} from "./types";
import { useSlotSelection } from "./useSlotSelection";
import { PriceRange, useStockPrices } from "./useStockPrices";
import { useStockOverrides } from "./useStockOverrides";

export interface StockOverrideModalProps {
  isOpen: boolean;
  onClose: () => void;
  conId: number;
  symbol?: string | null;
  initialOverrideId?: number;
  failureSlots?: string[];
}

const StockOverrideModal: React.FC<StockOverrideModalProps> = ({
  isOpen,
  onClose,
  conId,
  symbol,
  initialOverrideId,
  failureSlots,
}) => {
  const { theme } = useTheme();
  const toast = useToast();
  const isDarkMode = theme === "dark";

  const initialRange = useMemo(
    () => computeInitialRange(failureSlots),
    [failureSlots]
  );

  const {
    overrides,
    isLoading: overridesLoading,
    create,
    update,
    remove,
  } = useStockOverrides(conId, initialOverrideId);

  const {
    prices,
    isLoading: pricesLoading,
    range,
    fetchRange,
  } = useStockPrices(conId, initialRange);

  const { selected, toggle, toggleAll, selectSlots, clear } =
    useSlotSelection(failureSlots);

  const [pendingOverrides, setPendingOverrides] = useState<Record<string, string>>({});
  const [multValues, setMultValues] = useState<Record<string, string>>({});
  const [visibleSlots, setVisibleSlots] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [editingOverrideId, setEditingOverrideId] = useState<number | null>(
    initialOverrideId ?? null
  );
  const [activeTab, setActiveTab] = useState<"prices" | "overrides">("prices");
  const [deleteTarget, setDeleteTarget] = useState<number | "all" | null>(null);
  const [deleting, setDeleting] = useState(false);

  const failureSet = useMemo(
    () => new Set(failureSlots ?? []),
    [failureSlots]
  );

  const activeOverrides = useMemo(
    () => overrides.filter((o) => o.isActive),
    [overrides]
  );

  const rows: EnrichedPriceRow[] = useMemo(() => {
    const sorted = [...prices].sort(
      (a, b) =>
        new Date(a.datetimeUtc).getTime() - new Date(b.datetimeUtc).getTime()
    );
    return sorted.map((p) => {
      const ov = resolveOverrideForSlot(p.datetimeUtc, activeOverrides);

      let overrideValue: string | null = null;
      if (ov) {
        if (ov.adjustmentAbsolute) {
          overrideValue = ov.adjustmentAbsolute;
        } else if (ov.adjustmentFactor) {
          const base = parseFloat(p.close);
          const factor = parseFloat(ov.adjustmentFactor);
          if (!isNaN(base) && !isNaN(factor)) {
            overrideValue = formatNumberFixed(base * factor, 2);
          }
        }
      }

      return {
        ...p,
        isFailed: failureSet.has(p.datetimeUtc),
        isOverridden: !!ov,
        overrideId: ov?.id ?? null,
        overrideValue,
        isSelected: selected.has(p.datetimeUtc),
        pendingValue: pendingOverrides[p.datetimeUtc] ?? null,
      };
    });
  }, [prices, activeOverrides, failureSet, selected, pendingOverrides]);

  const visibleSet = useMemo(
    () => (visibleSlots === null ? null : new Set(visibleSlots)),
    [visibleSlots]
  );

  const selectedFailedSlots = useMemo(
    () =>
      rows
        .filter(
          (r) =>
            r.isSelected &&
            r.isFailed &&
            (visibleSet === null || visibleSet.has(r.datetimeUtc))
        )
        .map((r) => r.datetimeUtc),
    [rows, visibleSet]
  );

  const failedSlots = useMemo(
    () =>
      rows
        .filter(
          (r) => r.isFailed && (visibleSet === null || visibleSet.has(r.datetimeUtc))
        )
        .map((r) => r.datetimeUtc),
    [rows, visibleSet]
  );

  const pendingCount = Object.keys(pendingOverrides).length;

  // --- Time range handlers ---
  const handleShift = useCallback(
    (hours: number) => {
      const fromMs = new Date(range.from).getTime() + hours * 3600_000;
      const toMs = new Date(range.to).getTime() + hours * 3600_000;
      void fetchRange({
        from: new Date(fromMs).toISOString(),
        to: new Date(toMs).toISOString(),
      });
    },
    [range, fetchRange]
  );

  const handleRangeChange = useCallback(
    (r: PriceRange) => void fetchRange(r),
    [fetchRange]
  );

  // --- Pending override helpers (all local, no API) ---
  const setPending = useCallback((slots: string[], price: string) => {
    setPendingOverrides((prev) => {
      const next = { ...prev };
      for (const slot of slots) {
        next[slot] = price;
      }
      return next;
    });
  }, []);

  const handleCarryForward = useCallback(() => {
    const priceMap = findCarryForwardPrices(rows, selectedFailedSlots, failureSet);
    if (!priceMap) {
      toast.error("No good price found before the selected slots");
      return;
    }
    setPendingOverrides((prev) => ({ ...prev, ...priceMap }));
    clear();
  }, [rows, failureSet, selectedFailedSlots, toast, clear]);

  const handleManualEntry = useCallback(
    (price: string) => {
      setPending(selectedFailedSlots, price);
      clear();
    },
    [selectedFailedSlots, setPending, clear]
  );

  const handleDragApply = useCallback(
    (price: string, targetSlots: string[]) => {
      setPending(targetSlots, price);
    },
    [setPending]
  );

  const handleInlineEdit = useCallback(
    (slot: string, value: string) => {
      setPendingOverrides((prev) => {
        if (!value.trim()) {
          const next = { ...prev };
          delete next[slot];
          return next;
        }
        return { ...prev, [slot]: value };
      });
    },
    []
  );

  const handleMultiplier = useCallback(
    (slot: string, factor: number) => {
      const row = rows.find((r) => r.datetimeUtc === slot);
      if (!row) return;
      const base = parseFloat(row.close);
      if (isNaN(base)) return;
      const adjusted = formatNumberFixed(base * factor, 2);
      setPendingOverrides((prev) => ({ ...prev, [slot]: adjusted }));
    },
    [rows]
  );

  const handleMultChange = useCallback(
    (slot: string, raw: string) => {
      setMultValues((prev) => ({ ...prev, [slot]: raw }));
    },
    []
  );

  // --- Save: commit all pending to API ---
  // Only updates point overrides (start === end === slot).
  // Range overrides are left untouched; a new point override is created instead.
  const handleSave = useCallback(async () => {
    const entries = Object.entries(pendingOverrides).filter(
      ([, v]) => v.trim() && !Number.isNaN(Number(v))
    );
    if (entries.length === 0) return;

    const rowBySlot = new Map(rows.map((r) => [r.datetimeUtc, r]));
    const overrideById = new Map(activeOverrides.map((o) => [o.id, o]));

    setSaving(true);
    try {
      for (const [slot, price] of entries) {
        const row = rowBySlot.get(slot);
        const existingId = row?.overrideId ?? null;

        let isPointOverride = false;
        if (existingId !== null) {
          const existing = overrideById.get(existingId);
          isPointOverride = !!existing &&
            existing.startAtUtc === slot &&
            existing.endAtUtc === slot;
        }

        if (isPointOverride && existingId !== null) {
          const updateInput: StockOverrideUpdateInput = {
            adjustmentType: "MANUAL",
            adjustmentFactor: null,
            adjustmentAbsolute: price,
            isActive: true,
            reason: `Price fix: ${price}`,
          };
          await update(existingId, updateInput);
        } else {
          const createInput: StockOverrideCreateInput = {
            conId,
            source: "IBKR",
            startAtUtc: slot,
            endAtUtc: slot,
            adjustmentType: "MANUAL",
            adjustmentFactor: null,
            adjustmentAbsolute: price,
            reason: `Price fix: ${price}`,
          };
          await create(createInput);
        }
      }
      toast.success(`Saved ${entries.length} override(s)`);
      setPendingOverrides({});
      setMultValues({});
      void fetchRange(range);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save overrides");
    } finally {
      setSaving(false);
    }
  }, [pendingOverrides, rows, activeOverrides, conId, create, update, toast, fetchRange, range]);

  const handleDiscard = useCallback(() => {
    setPendingOverrides({});
    setMultValues({});
  }, []);

  // --- Existing override CRUD ---
  const editingOverride = overrides.find((o) => o.id === editingOverrideId) ?? null;

  const handleOverrideUpdate = useCallback(
    async (id: number, input: StockOverrideUpdateInput) => {
      try {
        await update(id, input);
        toast.success(`Override #${id} updated.`);
        setEditingOverrideId(null);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Failed to update override");
      }
    },
    [update, toast]
  );

  const handleOverrideCreate = useCallback(
    async (input: StockOverrideCreateInput) => {
      try {
        const created = await create(input);
        toast.success(`Override #${created.id} created.`);
        setEditingOverrideId(null);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Failed to create override");
      }
    },
    [create, toast]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (deleteTarget === null) return;
    setDeleting(true);
    try {
      if (deleteTarget === "all") {
        for (const ov of overrides) {
          await remove(ov.id);
        }
        toast.success(`Deleted all ${overrides.length} override(s).`);
      } else {
        await remove(deleteTarget);
        toast.success(`Override #${deleteTarget} deleted.`);
      }
      setDeleteTarget(null);
      setEditingOverrideId(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete override");
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, overrides, remove, toast]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-2"
      role="dialog"
      aria-modal="true"
      aria-label="Stock price data fix"
      data-testid="stock-override-modal"
    >
      <div
        className={`flex flex-col w-full h-full rounded-xl shadow-2xl overflow-hidden ${
          isDarkMode ? "bg-rp-surface" : "bg-white"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-rp-separator">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-bold text-gray-900 dark:text-white">
              Fix price data
            </h2>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {symbol ? `${symbol} · ` : ""}conId {conId}
            </span>
            {failureSlots && failureSlots.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded bg-gray-100 dark:bg-rp-surface-raised border border-gray-200 dark:border-rp-separator text-gray-500 dark:text-gray-400">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                {failureSlots.length} failed
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close override modal"
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-200 hover:bg-gray-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Time range */}
        <TimeRangeBar
          range={range}
          slotCount={rows.length}
          loading={pricesLoading}
          onShift={handleShift}
          onRangeChange={handleRangeChange}
        />

        {/* Chart -- updates in realtime as pending overrides change */}
        <div className="px-4 py-2 border-b border-gray-200 dark:border-rp-separator">
          <PriceChart rows={rows} isDarkMode={isDarkMode} />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-0 border-b border-gray-200 dark:border-rp-separator px-4">
          {([
            { key: "prices" as const, label: "Price Data" },
            { key: "overrides" as const, label: `Overrides (${overrides.length})` },
          ]).map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={
                "px-4 py-2 text-xs font-semibold transition-colors border-b-2 -mb-px " +
                (activeTab === tab.key
                  ? "border-rp-amber text-rp-amber"
                  : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200")
              }
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "prices" && (
          <>
            <ActionBar
              selectedCount={selectedFailedSlots.length}
              failedCount={failedSlots.length}
              hasSelection={selectedFailedSlots.length > 0}
              pendingCount={pendingCount}
              saving={saving}
              onCarryForward={handleCarryForward}
              onManualEntry={handleManualEntry}
              onSelectAllFailed={() => selectSlots(failedSlots)}
              onClearSelection={clear}
              onSave={handleSave}
              onDiscard={handleDiscard}
            />
            <div className="flex-1 min-h-0 overflow-auto">
              <PriceTable
                rows={rows}
                onToggleRow={toggle}
                onToggleAll={(visibleSlots) => toggleAll(visibleSlots)}
                selectedSlots={selected}
                onDragApply={handleDragApply}
                onInlineEdit={handleInlineEdit}
                onMultiplier={handleMultiplier}
                multValues={multValues}
                onMultChange={handleMultChange}
                onVisibleSlotsChange={setVisibleSlots}
              />
            </div>
          </>
        )}

        {activeTab === "overrides" && (
          <div className="flex-1 min-h-0 overflow-auto">
            <ExistingOverrides
              overrides={overrides}
              onEdit={setEditingOverrideId}
              onDelete={setDeleteTarget}
              onDeleteAll={() => setDeleteTarget("all")}
            />
          </div>
        )}
      </div>

      {/* Edit form overlay */}
      {editingOverrideId !== null && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4">
          <div
            className={`w-full max-w-lg rounded-xl shadow-2xl overflow-hidden ${
              isDarkMode ? "bg-rp-surface-raised" : "bg-white"
            }`}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-rp-separator">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                {editingOverride ? `Edit override #${editingOverrideId}` : "New override"}
              </h3>
              <button
                type="button"
                onClick={() => setEditingOverrideId(null)}
                className="p-1 rounded text-gray-400 hover:text-gray-200 hover:bg-gray-800 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {overridesLoading && !editingOverride ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-rp-amber border-t-transparent" />
                <span className="ml-2 text-sm text-gray-400">Loading override...</span>
              </div>
            ) : (
              <OverrideForm
                mode={editingOverride ? "edit" : "create"}
                initial={editingOverride}
                onCreate={handleOverrideCreate}
                onUpdate={handleOverrideUpdate}
                onDelete={(id) => setDeleteTarget(id)}
                isDarkMode={isDarkMode}
                conId={conId}
              />
            )}
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      <ConfirmationModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title={deleteTarget === "all" ? "Delete all overrides" : "Delete override"}
        message={
          deleteTarget === "all"
            ? `This will permanently remove all ${overrides.length} override(s) for this contract. This action cannot be undone.`
            : "This will permanently remove the override. This action cannot be undone."
        }
        confirmText={deleteTarget === "all" ? `Delete all (${overrides.length})` : "Delete"}
        isDestructive
        loading={deleting}
      />
    </div>
  );
};

export default StockOverrideModal;
